/**
 * src/lib/hooks/useBugReportChat.js — TICKET-1479. State for the chat's bug-report mode.
 *
 * One report per session of the mode: start() opens a draft on the server (which captures
 * route-independent auto context: last 15 min of this user's error rows, device, version),
 * send()/sendImage() run one interviewer turn each, and the report closes itself when the
 * server says plan_ready / incomplete. Every failure lands on the spine via logAppError.
 */
import { useCallback, useState } from 'react';
import { startBugReport, answerBugReport, uploadBugScreenshot, fetchMyBugReports, convertBugToIdea } from '../api/bugReports';
import { logAppError } from '../errorReporter';

/**
 * EN + ES copy for the bug-report UI. The components read `bugReport.<key>` through t()
 * with these as defaultValue; the same block lives in src/lib/i18n/{en,es}.json under
 * "bugReport" (the json wins). Keep both in step when copy changes.
 */
export const BUG_REPORT_STRINGS = {
  en: {
    entry: 'Report a problem',
    title: 'Problem report',
    titleIdea: 'Idea',
    titleHelp: 'Help',
    pickerLabel: 'What do you want to do?',
    modeBug: 'Bug',
    modeIdea: 'Idea',
    modeHelp: 'Help',
    convertIdea: 'Send as idea',
    doneIdea: 'Idea sent',
    exit: 'Back to chat',
    placeholder: 'Describe it in your own words…',
    send: 'Send',
    attach: 'Add screenshot',
    askScreenshot: 'Can you add a screenshot? Tap “Add screenshot”.',
    uploading: 'Uploading screenshot…',
    starting: 'Opening a report…',
    thinking: 'Thinking…',
    reference: 'Reference',
    done: 'Report sent',
    paused: 'The report assistant is paused right now. Your words are saved; try again later.',
    failed: 'Something went wrong. Please try again.',
    myReports: 'My reports',
    status_open: 'Open',
    status_in_progress: 'In progress',
    status_fixed: 'Fixed',
    status_closed: 'Closed',
  },
  es: {
    entry: 'Reportar un problema',
    title: 'Reporte de problema',
    titleIdea: 'Idea',
    titleHelp: 'Ayuda',
    pickerLabel: '¿Qué quieres hacer?',
    modeBug: 'Error',
    modeIdea: 'Idea',
    modeHelp: 'Ayuda',
    convertIdea: 'Enviar como idea',
    doneIdea: 'Idea enviada',
    exit: 'Volver al chat',
    placeholder: 'Descríbelo con tus palabras…',
    send: 'Enviar',
    attach: 'Añadir captura',
    askScreenshot: '¿Puedes subir una captura? Toca «Añadir captura».',
    uploading: 'Subiendo captura…',
    starting: 'Abriendo el reporte…',
    thinking: 'Pensando…',
    reference: 'Referencia',
    done: 'Reporte enviado',
    paused: 'El asistente de reportes está en pausa. Tus palabras quedan guardadas; inténtalo más tarde.',
    failed: 'Algo ha fallado. Inténtalo de nuevo.',
    myReports: 'Mis reportes',
    status_open: 'Abierto',
    status_in_progress: 'En curso',
    status_fixed: 'Arreglado',
    status_closed: 'Cerrado',
  },
};

/** t() with the bilingual default, so a missing json key never prints raw or mixes languages. */
export function bugText(t, locale, key) {
  const def = (BUG_REPORT_STRINGS[locale] || BUG_REPORT_STRINGS.en)[key] ?? BUG_REPORT_STRINGS.en[key] ?? key;
  return t(`bugReport.${key}`, { defaultValue: def });
}

const CLOSED = new Set(['plan_ready', 'incomplete', 'filed', 'in_progress', 'fixed', 'archived']);

function emit(err, step, extra = {}) {
  logAppError({ type: 'bug_report_failed', message: err?.message || String(err), view: 'AgentShell', metadata: { step, ...extra } });
}

export default function useBugReportChat({ language = 'es', api = {}, kind = 'bug' } = {}) {
  const start_ = api.startBugReport || startBugReport;
  const answer_ = api.answerBugReport || answerBugReport;
  const upload_ = api.uploadBugScreenshot || uploadBugScreenshot;
  const mine_ = api.fetchMyBugReports || fetchMyBugReports;
  const convert_ = api.convertBugToIdea || convertBugToIdea;

  const [reportRow, setReportRow] = useState(null);
  const [messages, setMessages] = useState([]); // [{ role: 'user'|'assistant', text, image? }]
  const [busy, setBusy] = useState(null); // null | 'starting' | 'thinking' | 'uploading'
  const [error, setError] = useState(null); // null | 'paused' | 'failed'
  const [wantsScreenshot, setWantsScreenshot] = useState(false);
  const [myReports, setMyReports] = useState([]);
  const [offerIdea, setOfferIdea] = useState(false); // help could not answer -> offer "Send as idea"
  const [liveKind, setLiveKind] = useState(kind);

  const done = !!reportRow && CLOSED.has(reportRow.status);

  const start = useCallback(async (route) => {
    setBusy('starting'); setError(null); setMessages([]); setReportRow(null); setWantsScreenshot(false); setOfferIdea(false); setLiveKind(kind);
    try {
      const r = await start_(kind === 'bug' ? { route, language } : { route, language, kind });
      if (!r?.report?.id) throw new Error(r?.error || 'start_failed');
      setReportRow(r.report);
      setMessages([{ role: 'assistant', text: r.question }]);
    } catch (e) {
      emit(e, 'bug_report.start');
      setError('failed');
    } finally {
      setBusy(null);
    }
  }, [start_, language, kind]);

  const turn = useCallback(async (text, imagePath) => {
    const r = await answer_(reportRow.id, text, imagePath);
    if (r?.killed) { setError('paused'); return; }
    if (!r?.success) throw new Error(r?.error || 'answer_failed');
    setReportRow(r.report);
    setWantsScreenshot(!!r.wants_screenshot);
    setOfferIdea(!!r.offer_idea);
    setMessages((m) => [...m, { role: 'assistant', text: r.reply }]);
  }, [answer_, reportRow]);

  const send = useCallback(async (raw) => {
    const text = String(raw || '').trim();
    if (!text || !reportRow || done || busy) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setBusy('thinking'); setError(null);
    try {
      await turn(text, null);
    } catch (e) {
      emit(e, 'bug_report.answer', { report_id: reportRow.id });
      setError('failed');
    } finally {
      setBusy(null);
    }
  }, [reportRow, done, busy, turn]);

  const sendImage = useCallback(async (file) => {
    if (!file || !reportRow || done || busy) return;
    setBusy('uploading'); setError(null);
    try {
      const path = await upload_(reportRow.id, file);
      setMessages((m) => [...m, { role: 'user', text: '', image: file.name || 'screenshot' }]);
      setBusy('thinking');
      await turn('', path);
    } catch (e) {
      emit(e, 'bug_report.screenshot', { report_id: reportRow.id });
      setError('failed');
    } finally {
      setBusy(null);
    }
  }, [reportRow, done, busy, upload_, turn]);

  const convertToIdea = useCallback(async () => {
    if (!reportRow || busy) return;
    setBusy('thinking'); setError(null);
    try {
      const r = await convert_(reportRow.id);
      if (!r?.report?.id) throw new Error(r?.error || 'convert_failed');
      setReportRow(r.report); setLiveKind('idea'); setOfferIdea(false);
      setMessages((m) => [...m, { role: 'assistant', text: r.question }]);
    } catch (e) {
      emit(e, 'bug_report.convert', { report_id: reportRow.id });
      setError('failed');
    } finally {
      setBusy(null);
    }
  }, [reportRow, busy, convert_]);

  const loadMine = useCallback(async () => {
    try {
      const r = await mine_();
      setMyReports(Array.isArray(r?.reports) ? r.reports : []);
    } catch (e) {
      emit(e, 'bug_report.mine');
    }
  }, [mine_]);

  return { kind: liveKind, offerIdea, convertToIdea, report: reportRow, messages, busy, error, done, wantsScreenshot, myReports, start, send, sendImage, loadMine };
}
